import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Check } from "lucide-react";
import { invokeFunction } from "@/integrations/neon/client";
import { toast } from "sonner";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { FREE_REVIEW_DATE_LIMIT } from "@/hooks/useSubscription";
import { setPendingUpgrade, takePendingUpgrade } from "@/lib/pendingUpgrade";

interface PricingProps {
  onSignUp: () => void;
}

const plans = [
  {
    name: "Free",
    price: "Free",
    description: "Everything you need to start decision journaling",
    features: [
      "Unlimited decision entries",
      `Up to ${FREE_REVIEW_DATE_LIMIT} review dates per decision`,
      "Analytics, recommendations & trends",
      "Custom tags and emotions recording",
      "Export capabilities (csv or json)"
    ],
    cta: "Get Started Free",
    variant: "outline" as const
  },
  {
    name: "Annual",
    price: "$25",
    period: " /year",
    description: "Best value for committed decision makers",
    features: [
      "Everything in Free",
      "Unlimited review dates per decision",
      "Full activity log",
      "Review reminders",
      "Email support"
    ],
    cta: "Start Annual Plan",
    variant: "default" as const,
    popular: true
  },
  {
    name: "Lifetime",
    price: "$100",
    period: " /one time",
    description: "One-time payment for lifetime access",
    features: [
      "Everything in Annual",
      "+ Priority support",
      "+ Early access to new features",
      "+ No recurring payments",
      "Transferable license if you change email"
    ],
    cta: "Get Lifetime Access",
    variant: "outline" as const
  }
];

export function Pricing({ onSignUp }: PricingProps) {
  const { user } = useAuth();
  const [loading, setLoading] = useState<string | null>(null);
  const [couponCode, setCouponCode] = useState("");
  const [showCouponInput, setShowCouponInput] = useState(false);

  const handleCheckout = useCallback(
    async (planType: 'annual' | 'lifetime', code?: string) => {
      if (!user) {
        // Checkout needs an account to attach the plan to. Remember which plan
        // was picked so it survives the sign-up round trip instead of the
        // click being silently dropped.
        setPendingUpgrade(planType);
        onSignUp();
        return;
      }

      setLoading(planType);
      try {
        const body: { planType: string; couponCode?: string } = { planType };
        const trimmed = (code ?? '').trim();
        if (trimmed) {
          body.couponCode = trimmed;
        }

        const { data, error } = await invokeFunction<{ url: string }>('/create-checkout', {
          body
        });

        if (error) throw error;

        if (data?.url) {
          // Redirect to Stripe checkout in same window
          window.location.href = data.url;
        } else {
          throw new Error('No checkout URL received');
        }
      } catch (error) {
        console.error('Checkout error:', error);
        toast.error(error instanceof Error ? error.message : 'Failed to create checkout session', {
          duration: 10000,
        });
        setLoading(null);
      }
      // No `finally`: on the success path the browser is navigating to Stripe,
      // and clearing the state there flips the button back to its idle label
      // for the second or two before the page goes away, which reads as the
      // click having done nothing.
    },
    [user, onSignUp],
  );

  // Resume a plan picked before signing up. App.tsx routes here once the user
  // is authenticated; the intent is consumed on read, so a cancelled or failed
  // checkout does not reopen Stripe on the next render.
  const resumedRef = useRef(false);
  useEffect(() => {
    if (!user || resumedRef.current) return;
    const pending = takePendingUpgrade();
    if (!pending) return;
    resumedRef.current = true;
    handleCheckout(pending);
  }, [user, handleCheckout]);

  const handleButtonClick = (plan: (typeof plans)[number]) => {
    if (plan.name === 'Free') {
      onSignUp();
    } else if (plan.name === 'Annual') {
      handleCheckout('annual', couponCode);
    } else if (plan.name === 'Lifetime') {
      handleCheckout('lifetime', couponCode);
    }
  };

  return (
    <section id="pricing" className="py-20 bg-muted/30">
      <div className="container">
        <div className="text-center mb-16">
          <h2 className="mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-muted-foreground">
            Choose the plan that fits your decision-making journey
          </p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {plans.map((plan, index) => (
            <Card key={index} className={`relative ${plan.popular ? 'border-primary shadow-lg' : 'border-0 shadow-sm'}`}>
              {plan.popular && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2">
                  Best Value
                </Badge>
              )}
              
              <CardHeader className="text-center">
                <CardTitle className="text-lg">{plan.name}</CardTitle>
                <div className="mt-4">
                  <span className="text-3xl font-medium">{plan.price}</span>
                  {plan.period && <span className="text-muted-foreground">{plan.period}</span>}
                </div>
                <CardDescription className="mt-2">
                  {plan.description}
                </CardDescription>
              </CardHeader>
              
              <CardContent>
                <Button 
                  className="w-full mb-6" 
                  variant={plan.variant}
                  onClick={() => handleButtonClick(plan)}
                  disabled={loading !== null}
                >
                  {loading === plan.name.toLowerCase() ? 'Processing...' : plan.cta}
                </Button>
                
                <ul className="space-y-3">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center">
                      <Check className="h-4 w-4 text-primary mr-3 flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
        
        <div className="text-center mt-12 space-y-4">
          <div className="max-w-sm mx-auto">
            {!showCouponInput ? (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowCouponInput(true)}
                className="text-sm"
              >
                Have a coupon code?
              </Button>
            ) : (
              <div className="flex gap-2">
                <Input
                  placeholder="Enter coupon code"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                  className="text-sm"
                />
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setShowCouponInput(false);
                    setCouponCode("");
                  }}
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            All paid plans include 30-day money-back guarantee
          </p>
        </div>
      </div>
    </section>
  );
}